import { useState, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useInView,
} from "framer-motion";
import AnimatedHeading from "../../animations/AnimatedHeading";
import { useNavigate } from "react-router-dom";

/* ============================================================
   STATS
============================================================ */

const stats = [
  {
    icon: "⭐",
    value: "4.9",
    sub: "Clutch rating",
    detail: "based on 30+ client reviews",
  },
  {
    icon: "🏆",
    value: "97%",
    sub: "of customers",
    detail: "are ready to recommend us",
  },
  {
    icon: "👥",
    value: "100%",
    sub: "of developers",
    detail: "have a degree in engineering",
  },
  {
    icon: "🚀",
    value: "600+",
    sub: "projects",
    detail: "successfully delivered",
  },
];

/* ============================================================
   INDUSTRIES
============================================================ */

const industries = [
  "All",
  "Real Estate",
  "Fintech",
  "eCommerce",
  "Healthcare",
  "SaaS",
  "EdTech",
];

/* ============================================================
   PROJECT DATA
============================================================ */

const projects = [
  {
    id: 1,
    title: "Real Estate Marketplace for StreetEasy",
    location: "USA",
    industry: "Real Estate",
    services: ["Web Development", "Mobile Development"],
    technologies: [
      "Ruby on Rails",
      "MySQL",
      "GraphQL",
      "Elastic Search",
      "Redis",
      "Memcached",
      "React",
      "Splunk",
    ],
    image:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=85",
    tag: "Featured",
    year: "2023",
  },

  {
    id: 2,
    title: "Comprehensive Agentic AI Platform for Predictive Trading",
    location: "US",
    industry: "Fintech",
    services: [
      "Web Development",
      "Mobile Development",
      "UI/UX Design",
    ],
    technologies: [
      "Python",
      "React Native",
      "Claude",
      "OpenAI LLM",
    ],
    image:
      "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=85",
    tag: "AI",
    year: "2024",
  },

  {
    id: 3,
    title: "Visdeal Multi-Market E-commerce Platform",
    location: "Netherlands",
    industry: "eCommerce",
    services: ["Web Development"],
    technologies: [
      "Next.js",
      "Node.js",
      "PostgreSQL",
      "Stripe",
      "Redis",
    ],
    image:
      "https://images.unsplash.com/photo-1688561807440-8a57dfa77ee3?w=1200&q=85",
    tag: "eCommerce",
    year: "2023",
  },

  {
    id: 4,
    title: "MediTrack Patient Management System",
    location: "UK",
    industry: "Healthcare",
    services: [
      "Web Development",
      "UI/UX Design",
    ],
    technologies: [
      "React",
      "Django",
      "PostgreSQL",
      "AWS",
      "Docker",
    ],
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=85",
    tag: "Healthcare",
    year: "2024",
  },

  {
    id: 5,
    title: "CloudFlow SaaS Project Management Suite",
    location: "Canada",
    industry: "SaaS",
    services: [
      "Web Development",
      "UI/UX Design",
      "DevOps",
    ],
    technologies: [
      "Vue.js",
      "Node.js",
      "MongoDB",
      "Kubernetes",
      "GitHub Actions",
    ],
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=85",
    tag: "SaaS",
    year: "2023",
  },

  {
    id: 6,
    title: "LearnSphere Adaptive EdTech Platform",
    location: "Australia",
    industry: "EdTech",
    services: [
      "Web Development",
      "Mobile Development",
      "AI & ML",
    ],
    technologies: [
      "React",
      "Python",
      "TensorFlow",
      "Firebase",
      "Flutter",
    ],
    image:
      "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=1200&q=85",
    tag: "AI",
    year: "2024",
  },

  {
    id: 7,
    title: "FinVault Cross-Border Payment Infrastructure",
    location: "Singapore",
    industry: "Fintech",
    services: [
      "Web Development",
      "DevOps",
    ],
    technologies: [
      "Go",
      "React",
      "Kafka",
      "PostgreSQL",
      "Terraform",
      "AWS",
    ],
    image:
      "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&q=85",
    tag: "Fintech",
    year: "2024",
  },

  {
    id: 8,
    title: "LuxeStay Premium Property Rental App",
    location: "UAE",
    industry: "Real Estate",
    services: [
      "Mobile Development",
      "UI/UX Design",
    ],
    technologies: [
      "React Native",
      "Node.js",
      "MongoDB",
      "Stripe",
      "Mapbox",
    ],
    image:
      "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&q=85",
    tag: "Mobile",
    year: "2023",
  },
];

/* ============================================================
   ANIMATION
============================================================ */

const ease = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 36,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease,
    },
  },
};

const stagger = {
  hidden: {},

  visible: {
    transition: {
      staggerChildren: 0.09,
    },
  },
};

/* ============================================================
   REVEAL
============================================================ */

function Reveal({
  children,
  variants = fadeUp,
  className = "",
}) {
  const ref = useRef(null);

  const inView = useInView(ref, {
    once: true,
    amount: 0.15,
  });

  return (
    <motion.div
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ============================================================
   STARS
============================================================ */

function Stars({ count = 5 }) {
  return (
    <div className="flex items-center gap-0.5 h-7">
      {[...Array(count)].map((_, i) => (
        <svg
          key={i}
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="text-black"
          style={{
            opacity: 0.85,
            display: "block",
          }}
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

/* ============================================================
   PROJECT CARD
============================================================ */

function ProjectCard({ project }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Reveal variants={fadeUp}>
      <motion.div
        whileHover={{
          y:
            typeof window !== "undefined" &&
            window.innerWidth >= 1024
              ? -4
              : 0,
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="
          group
          w-full
          overflow-hidden
          rounded-2xl
          flex
          flex-col
          lg:flex-row
          cursor-pointer
          theme-card
          theme-border
          project-card
        "
        style={{
          /*
           * ======================================================
           * SLIGHT GRAY PROJECT CARD BACKGROUND
           * ======================================================
           *
           * The card is only slightly darker/lighter than the
           * page background so it doesn't blend into the page.
           *
           * It automatically adapts to light/dark theme because
           * it uses your existing theme variables.
           */
          background:
            "color-mix(in srgb, var(--bg-primary) 96%, var(--text-primary) 4%)",

          border: hovered
            ? "1px solid rgba(249,115,22,0.45)"
            : "1px solid var(--border-primary)",

          boxShadow:
            hovered &&
            typeof window !== "undefined" &&
            window.innerWidth >= 1024
              ? "0 0 32px rgba(249,115,22,0.12)"
              : "none",

          transition:
            "background .25s, border-color .25s, box-shadow .25s, transform .25s",
        }}
      >
        {/* =====================================================
            CONTENT
        ====================================================== */}

        <div
          className="
            order-2
            flex
            min-w-0
            flex-1
            flex-col

            p-3.5

            min-[375px]:p-4

            sm:p-4.5

            md:p-5

            lg:order-1
            lg:p-8
          "
        >
          {/* Tag + Year */}

          <div
            className="
              flex
              flex-wrap
              items-center
              gap-2
              mb-2.5

              min-[375px]:mb-3

              sm:mb-3

              lg:mb-4
            "
          >
            <span
              className="
                text-[8px]
                min-[375px]:text-[9px]
                sm:text-[10px]
                font-extrabold
                uppercase
                tracking-widest
                px-2
                sm:px-2.5
                py-0.5
                sm:py-1
                rounded-full
                text-black
              "
              style={{
                background:
                  "linear-gradient(135deg,var(--brand-primary),#c2410c)",
              }}
            >
              {project.tag}
            </span>

            <span
              className="
                text-[10px]
                sm:text-xs
                theme-text-muted
              "
              style={{
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              {project.year}
            </span>
          </div>

          {/* Title */}

          <h3
            className="
              font-extrabold
              theme-text-primary
              mb-3
              min-[375px]:mb-3.5
              sm:mb-4
              md:mb-4
              lg:mb-5

              leading-tight

              text-[16px]

              min-[375px]:text-[17px]

              sm:text-lg

              md:text-[20px]

              lg:text-2xl

              break-words
            "
            style={{
              fontFamily: "'Saira', sans-serif",
            }}
          >
            {project.title}
          </h3>

          {/* Divider */}

          <div
            className="
              mb-3
              sm:mb-3.5
              md:mb-4
              lg:mb-5
            "
            style={{
              height: 1,
              background: "var(--border-secondary)",
            }}
          />

          {/* =================================================
              PROJECT DETAILS
          ================================================= */}

          <div
            className="
              space-y-1.5
              mb-3

              min-[375px]:space-y-2
              min-[375px]:mb-3.5

              sm:space-y-2
              sm:mb-4

              md:space-y-2
              md:mb-5

              lg:mb-6
            "
          >
            {[
              {
                label: "Location",
                value: project.location,
              },
              {
                label: "Industry",
                value: project.industry,
              },
              {
                label: "Services",
                value: project.services.join(", "),
              },
            ].map(({ label, value }) => (
              <div
                key={label}
                className="
                  flex
                  gap-2
                  sm:gap-2.5
                  md:gap-3
                  text-[10px]
                  min-[375px]:text-[11px]
                  sm:text-xs
                  md:text-sm
                  min-w-0
                  leading-relaxed
                "
              >
                <span
                  className="
                    shrink-0
                    w-[62px]

                    min-[375px]:w-[68px]

                    sm:w-[76px]

                    md:w-[82px]

                    lg:min-w-[95px]

                    theme-text-muted
                  "
                  style={{
                    fontFamily: "'DM Sans',sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {label}:
                </span>

                <span
                  className="
                    min-w-0
                    break-words
                    brand-color
                  "
                  style={{
                    fontFamily: "'DM Sans',sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {value}
                </span>
              </div>
            ))}

            {/* Technologies */}

            <div
              className="
                flex
                gap-2
                sm:gap-2.5
                md:gap-3
                text-[10px]
                min-[375px]:text-[11px]
                sm:text-xs
                md:text-sm
                flex-wrap
                min-w-0
              "
            >
              <span
                className="
                  shrink-0
                  w-[62px]

                  min-[375px]:w-[68px]

                  sm:w-[76px]

                  md:w-[82px]

                  lg:min-w-[95px]

                  theme-text-muted
                "
                style={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontWeight: 500,
                }}
              >
                Technologies:
              </span>

              <div
                className="
                  flex
                  flex-1
                  min-w-0
                  flex-wrap
                  gap-1

                  sm:gap-1
                "
              >
                {project.technologies.map((t, i) => (
                  <span
                    key={i}
                    className="
                      text-[8px]
                      min-[375px]:text-[9px]
                      sm:text-[9px]
                      md:text-[10px]
                      lg:text-xs

                      px-1.5
                      sm:px-1.5
                      md:px-2

                      py-0.5

                      rounded-full
                      whitespace-nowrap

                      brand-color
                    "
                    style={{
                      background:
                        "rgba(249,115,22,0.08)",
                      border:
                        "1px solid rgba(249,115,22,0.2)",
                      fontFamily: "'DM Sans',sans-serif",
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Project Overview */}

          <motion.div
            animate={{
              x: hovered ? 4 : 0,
            }}
            transition={{
              duration: 0.2,
            }}
            className="
              flex
              items-center
              gap-2
              text-[10px]
              min-[375px]:text-[11px]
              sm:text-xs
              md:text-sm
              font-bold
              brand-color
              mt-auto
            "
            style={{
              fontFamily: "'Syne',sans-serif",
            }}
          >
            Project Overview
            <span className="brand-color">→</span>
          </motion.div>
        </div>

        {/* =====================================================
            IMAGE
        ====================================================== */}

        <div
          className="
            order-1
            relative
            w-full
            overflow-hidden

            h-[125px]

            min-[375px]:h-[135px]

            sm:h-[145px]

            md:h-[160px]

            lg:order-2
            lg:h-auto
            lg:min-h-[360px]
            lg:w-[42%]
          "
          style={{
            minHeight: 0,
          }}
        >
          <motion.img
            src={project.image}
            alt={project.title}
            animate={{
              scale: hovered ? 1.06 : 1,
            }}
            transition={{
              duration: 0.5,
              ease,
            }}
            className="
              absolute
              inset-0
              w-full
              h-full
              object-cover
            "
            style={{
              filter: "brightness(.55) saturate(.65)",
            }}
          />

          {/* Image overlay */}

          <div
            className="
              absolute
              inset-0
            "
            style={{
              background:
                "linear-gradient(to bottom,rgba(15,15,15,0.05),rgba(15,15,15,0.65))",
            }}
          />

          {/* Desktop left gradient */}

          <div
            className="
              absolute
              inset-y-0
              left-0
              hidden
              w-1/2

              lg:block
            "
            style={{
              background:
                "linear-gradient(to right,rgba(15,15,15,0.75),transparent)",
            }}
          />

          {/* Orange corner glow */}

          <div
            className="
              absolute
              top-0
              right-0
              w-14
              h-14

              sm:w-16
              sm:h-16

              md:w-20
              md:h-20
            "
            style={{
              background:
                "linear-gradient(225deg,rgba(249,115,22,0.18),transparent)",
            }}
          />
        </div>
      </motion.div>
    </Reveal>
  );
}

/* ============================================================
   DROPDOWN
============================================================ */

function Dropdown({
  label,
  options,
  selected,
  onSelect,
}) {
  const [open, setOpen] = useState(false);

  const isActive = selected !== "All";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="
          flex
          items-center
          gap-2
          px-4
          py-2.5
          sm:px-5
          rounded-full
          text-xs
          sm:text-sm
          font-semibold
          cursor-pointer
          border-0
          whitespace-nowrap
        "
        style={{
          background: isActive
            ? "linear-gradient(135deg,var(--brand-primary),#c2410c)"
            : "var(--card-bg)",

          color: isActive
            ? "#000"
            : "var(--text-muted)",

          border: isActive
            ? "none"
            : "1px solid var(--border-primary)",

          boxShadow: isActive
            ? "0 0 16px rgba(249,115,22,0.4)"
            : "none",

          fontFamily: "'Syne',sans-serif",

          transition: "all .2s",
        }}
      >
        {isActive ? selected : label}

        <motion.svg
          animate={{
            rotate: open ? 180 : 0,
          }}
          transition={{
            duration: 0.2,
          }}
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke={isActive ? "#000" : "var(--text-muted)"}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </motion.svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{
              opacity: 0,
              y: -8,
              scale: 0.96,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: -8,
              scale: 0.96,
            }}
            transition={{
              duration: 0.18,
            }}
            className="
              absolute
              left-0
              top-full
              mt-2
              rounded-xl
              overflow-hidden
              z-50
              max-w-[calc(100vw-32px)]
              theme-card
              theme-border
            "
            style={{
              minWidth: 180,
              boxShadow:
                "0 20px 40px var(--shadow-color)",
            }}
          >
            {options.map((opt) => (
              <button
                type="button"
                key={opt}
                onClick={() => {
                  onSelect(opt);
                  setOpen(false);
                }}
                className="
                  w-full
                  text-left
                  px-4
                  py-2.5
                  text-xs
                  sm:text-sm
                  cursor-pointer
                  border-0
                "
                style={{
                  background:
                    selected === opt
                      ? "rgba(249,115,22,0.1)"
                      : "transparent",

                  color:
                    selected === opt
                      ? "var(--brand-primary)"
                      : "var(--text-muted)",

                  fontFamily:
                    "'DM Sans',sans-serif",

                  transition:
                    "background .15s",
                }}
                onMouseEnter={(e) => {
                  if (selected !== opt) {
                    e.currentTarget.style.background =
                      "var(--bg-tertiary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selected !== opt) {
                    e.currentTarget.style.background =
                      "transparent";
                  }
                }}
              >
                {opt}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ============================================================
   PORTFOLIO PAGE
============================================================ */

export default function PortfolioPage() {
  const [selectedIndustry, setSelectedIndustry] =
    useState("All");

  const navigate = useNavigate();

  const filtered = projects.filter((p) => {
    return (
      selectedIndustry === "All" ||
      p.industry === selectedIndustry
    );
  });

  return (
    <div
      className="
        min-h-screen
        w-full
        overflow-x-hidden
        theme-bg-primary
      "
      style={{
        fontFamily: "'Syne',sans-serif",
      }}
    >
      {/* ========================================================
          GLOBAL STYLES
      ========================================================= */}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

        .body-font {
          font-family:'DM Sans',sans-serif !important;
        }

        ::-webkit-scrollbar {
          width:3px;
        }

        ::-webkit-scrollbar-track {
          background:var(--bg-primary);
        }

        ::-webkit-scrollbar-thumb {
          background:var(--brand-primary);
          border-radius:2px;
        }

        .oglow {
          box-shadow:
            0 0 22px rgba(249,115,22,.45),
            0 0 55px rgba(249,115,22,.15);
        }

        .orange-grad {
          background:linear-gradient(
            135deg,
            var(--brand-primary),
            #c2410c
          );
        }

        .orange-text-grad {
          background:linear-gradient(
            135deg,
            var(--brand-primary),
            #fbbf24
          );

          -webkit-background-clip:text;
          -webkit-text-fill-color:transparent;
        }

        .hero-grid {
          background-image:
            linear-gradient(
              rgba(249,115,22,.04) 1px,
              transparent 1px
            ),
            linear-gradient(
              90deg,
              rgba(249,115,22,.04) 1px,
              transparent 1px
            );

          background-size:56px 56px;
        }

        .portfolio-project-content {
          min-width:0;
          overflow-wrap:anywhere;
        }

        /*
         * ======================================================
         * PROJECT CARD BACKGROUND
         * ======================================================
         *
         * Gives every project card a very subtle gray tint
         * instead of allowing it to blend into the page.
         *
         * The actual background is applied inline on the card
         * so it takes priority over the existing theme-card class.
         */

        .project-card {
          isolation:isolate;
        }

        button {
          -webkit-tap-highlight-color:transparent;
        }
      `}</style>

      {/* ========================================================
          HERO
      ========================================================= */}

      <section
        className="
          relative
          overflow-hidden
          text-center

          pt-16
          pb-10
          px-4

          min-[375px]:pt-[72px]
          min-[375px]:pb-11
          min-[375px]:px-5

          sm:pt-16
          sm:pb-12
          sm:px-6

          md:pt-16
          md:pb-12

          lg:pt-12
          lg:pb-12
          lg:px-6
        "
      >
        {/* Grid */}

        <div className="hero-grid absolute inset-0" />

        {/* Glow */}

        <div
          className="
            absolute
            top-0
            left-1/2
            -translate-x-1/2
            rounded-full

            w-[300px]
            h-[180px]

            sm:w-[450px]
            sm:h-[240px]

            lg:w-[600px]
            lg:h-[300px]
          "
          style={{
            background:
              "radial-gradient(ellipse,rgba(249,115,22,.12),transparent 70%)",
            filter: "blur(40px)",
          }}
        />

        <div
          className="
            relative
            z-10
            mx-auto
            w-full
            max-w-2xl
          "
        >
          {/* Eyebrow */}

          <motion.p
            initial={{
              opacity: 0,
              y: 12,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.4,
            }}
            className="
              brand-color
              text-[10px]

              min-[375px]:text-[11px]

              sm:text-xs

              md:text-sm

              tracking-[0.22em]
              sm:tracking-[0.3em]

              uppercase
              font-bold

              mb-4

              sm:mb-5

              lg:mb-6
            "
          >
            [ Our Work ]
          </motion.p>

          {/* Heading */}

          <AnimatedHeading
            as="h2"
            parts={["Our Projects"]}
            className="
              font-saira
              theme-text-primary
              text-3xl

              min-[375px]:text-[32px]

              sm:text-4xl

              md:text-5xl

              lg:text-6xl

              font-semibold
              leading-tight

              mb-4

              sm:mb-5

              lg:mb-6
            "
          />

          {/* Description */}

          <motion.p
            initial={{
              opacity: 0,
              y: 16,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.55,
              delay: 0.14,
            }}
            className="
              body-font
              theme-text-muted
              text-xs

              min-[375px]:text-sm

              md:text-base

              leading-relaxed

              max-w-xl
              mx-auto
            "
          >
            Our software company portfolio is a gallery of
            projects that transformed ideas into groundbreaking
            solutions. View our curated collection of case
            studies and see what we can build for you.
          </motion.p>
        </div>
      </section>

      {/* ========================================================
          STATS BANNER
      ========================================================= */}

      <section
        className="
          px-4

          min-[375px]:px-5

          sm:px-6

          pb-10

          sm:pb-12

          md:pb-14

          lg:pb-14
        "
      >
        <div
          className="
            max-w-5xl
            mx-auto
            w-full
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
              delay: 0.2,
            }}
            className="
              rounded-2xl
              overflow-hidden
              w-full
            "
            style={{
              background:
                "linear-gradient(135deg,var(--brand-primary),#ea580c,#c2410c)",
            }}
          >
            <div
              className="
                grid
                grid-cols-2

                lg:grid-cols-4
              "
            >
              {stats.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.5,
                    delay: 0.25 + i * 0.07,
                  }}
                  className="
                    p-4

                    min-[375px]:p-5

                    sm:p-6

                    md:p-7

                    lg:p-7

                    text-left
                  "
                  style={{
                    borderRight:
                      i < 3
                        ? "1px solid rgba(0,0,0,0.15)"
                        : "none",

                    borderBottom:
                      i < 2
                        ? "1px solid rgba(0,0,0,0.15)"
                        : "none",
                  }}
                >
                  {/* Fixed Icon Area */}

                  <div
                    className="
                      h-7
                      flex
                      items-center
                      mb-2
                    "
                  >
                    {i === 0 ? (
                      <Stars count={5} />
                    ) : (
                      <div
                        className="
                          flex
                          items-center
                          justify-start
                          text-xl
                          sm:text-2xl
                        "
                        style={{
                          opacity: 0.6,
                        }}
                      >
                        {s.icon}
                      </div>
                    )}
                  </div>

                  {/* Value */}

                  <div
                    className="
                      font-extrabold
                      text-black
                      mb-0.5
                    "
                    style={{
                      fontSize:
                        "clamp(1.55rem,3vw,2.5rem)",
                      lineHeight: 1,
                    }}
                  >
                    {s.value}
                  </div>

                  {/* Sub */}

                  <div
                    className="
                      text-black
                      font-semibold

                      text-xs

                      sm:text-sm

                      mb-1
                    "
                    style={{
                      opacity: 0.7,
                    }}
                  >
                    {s.sub}
                  </div>

                  {/* Detail */}

                  <div
                    className="
                      text-black
                      text-[10px]

                      sm:text-xs

                      leading-snug
                    "
                    style={{
                      opacity: 0.55,
                      fontFamily:
                        "'DM Sans',sans-serif",
                    }}
                  >
                    {s.detail}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========================================================
          FILTERS
      ========================================================= */}

      <section
        className="
          px-4

          min-[375px]:px-5

          sm:px-6

          pb-6

          sm:pb-7

          lg:pb-8
        "
      >
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div
              className="
                flex
                flex-wrap
                items-center
                gap-2.5

                sm:gap-3
              "
            >
              <Dropdown
                label="Industries ↓"
                options={industries}
                selected={selectedIndustry}
                onSelect={setSelectedIndustry}
              />

              {selectedIndustry !== "All" && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedIndustry("All");
                  }}
                  className="
                    px-3.5
                    sm:px-4
                    py-2
                    rounded-full
                    text-[10px]
                    sm:text-xs
                    font-semibold
                    cursor-pointer
                    border-0
                    whitespace-nowrap
                  "
                  style={{
                    background:
                      "var(--bg-tertiary)",
                    color: "var(--text-muted)",
                    border:
                      "1px solid var(--border-primary)",
                    transition: "color .2s",
                    fontFamily:
                      "'DM Sans',sans-serif",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color =
                      "var(--brand-primary)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color =
                      "var(--text-muted)")
                  }
                >
                  ✕ Clear filters
                </button>
              )}

              <span
                className="
                  body-font
                  theme-text-muted
                  text-[10px]
                  sm:text-xs
                  ml-auto
                "
              >
                {filtered.length} project
                {filtered.length !== 1
                  ? "s"
                  : ""}
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========================================================
          PROJECTS
      ========================================================= */}

      <section
        className="
          px-4

          min-[375px]:px-5

          sm:px-6

          pb-16

          sm:pb-20

          lg:pb-24
        "
      >
        <div className="max-w-5xl mx-auto">
          <AnimatePresence mode="wait">
            {filtered.length === 0 ? (
              <motion.div
                key="empty"
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
                className="
                  text-center
                  py-16

                  sm:py-20
                "
              >
                <div className="text-4xl sm:text-5xl mb-4">
                  🔍
                </div>

                <p className="font-bold theme-text-primary text-base sm:text-lg mb-2">
                  No projects found
                </p>

                <p
                  className="
                    body-font
                    theme-text-muted
                    text-xs
                    sm:text-sm
                  "
                >
                  Try adjusting your filters
                </p>
              </motion.div>
            ) : (
              <motion.div
                key={`${selectedIndustry}`}
                variants={stagger}
                initial="hidden"
                animate="visible"
                className="
                  flex
                  flex-col

                  gap-3.5

                  min-[375px]:gap-4

                  sm:gap-4

                  md:gap-5
                "
              >
                {filtered
                  .slice(0, 5)
                  .map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                    />
                  ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ========================================================
          CTA
      ========================================================= */}

      <section
        className="
          relative
          overflow-hidden

          py-16
          px-4

          min-[375px]:py-18
          min-[375px]:px-5

          sm:py-20
          sm:px-6

          lg:py-24
        "
        style={{
          borderTop:
            "1px solid var(--border-secondary)",
        }}
      >
        <div className="hero-grid absolute inset-0" />

        <div
          className="
            absolute
            top-1/2
            left-1/2
            -translate-x-1/2
            -translate-y-1/2
            rounded-full

            w-[300px]
            h-[150px]

            sm:w-[400px]
            sm:h-[180px]

            lg:w-[500px]
            lg:h-[200px]
          "
          style={{
            background:
              "radial-gradient(ellipse,rgba(249,115,22,.14),transparent 70%)",
            filter: "blur(40px)",
          }}
        />

        <div
          className="
            relative
            z-10
            text-center
            max-w-2xl
            mx-auto
          "
        >
          <Reveal>
            <p
              className="
                brand-color
                text-[10px]

                sm:text-xs

                font-bold
                tracking-widest
                uppercase
                mb-3

                sm:mb-4
              "
            >
              Start Your Project
            </p>

            <h2
              className="
                font-extrabold
                theme-text-primary

                mb-4

                sm:mb-5
              "
              style={{
                fontSize:
                  "clamp(1.9rem,5vw,2.8rem)",
                lineHeight: 1.1,
              }}
            >
              Have a Project{" "}
              <span className="orange-text-grad">
                in Mind?
              </span>
            </h2>

            <p
              className="
                body-font
                theme-text-muted
                text-sm

                sm:text-base

                leading-relaxed
                mb-7

                sm:mb-10

                max-w-lg
                mx-auto
              "
            >
              Let's talk about your idea. We'll
              help you define the scope, choose
              the right tech stack, and build
              something extraordinary.
            </p>

            <div
              className="
                flex
                gap-3
                justify-center
                flex-wrap
              "
            >
              <button
                type="button"
                onClick={() => {
                  navigate(
                    "/contact#contact-form"
                  );
                }}
                className="
                  oglow
                  orange-grad
                  text-black
                  font-bold

                  px-7
                  py-3

                  sm:px-9
                  sm:py-4

                  rounded-full
                  text-xs
                  sm:text-sm
                  cursor-pointer
                  border-0
                  transition-transform
                  duration-300
                  hover:scale-105
                  active:scale-95
                "
              >
                Start a Project →
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}